import type { ApiTitle } from "@lumi/data";
import type { Variants } from "framer-motion";
import { useEffect, useState, type ReactElement } from "react";
import { apiCall } from "../../../config/api";
import { CollectionRail } from "./CollectionRail";

/**
 * New Releases rail - displays recently added content from new-releases collection
 * Auto-populated by sync-new-releases.mjs script
 */

interface CollectionResponse {
  collection: {
    key: string;
    name: string;
    sort: number;
    cardType: "VERTICAL" | "HORIZONTAL";
  };
  items: Array<{
    rank: number;
    title: ApiTitle;
  }>;
}

interface NewReleasesRailProps {
  readonly transitionTo: (path: string, variant?: Variants) => void;
}

export function NewReleasesRail({ transitionTo }: NewReleasesRailProps): ReactElement {
  const [titles, setTitles] = useState<ApiTitle[]>([]);
  // Start with HORIZONTAL as default since most collections use it
  const [cardType, setCardType] = useState<"VERTICAL" | "HORIZONTAL">("HORIZONTAL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadNewReleases(): Promise<void> {
      try {
        const response = await apiCall("/v1/collections/new-releases");
        if (!response.ok) {
          if (response.status === 404) {
            // Collection doesn't exist yet - show empty state
            console.warn("⚠️ new-releases collection not found");
            setTitles([]);
            setLoading(false);
            return;
          }
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }

        const data = (await response.json()) as CollectionResponse;
        const newTitles = data.items.map((item) => item.title);

        console.log("✅ New releases loaded:", newTitles.length);
        setTitles(newTitles);
        setCardType(data.collection.cardType);
        setLoading(false);
      } catch (err) {
        console.error("❌ Failed to load new releases:", err);
        setError("Failed to load new releases");
        setLoading(false);
      }
    }

    void loadNewReleases();
  }, []);

  // Don't render if there was an error
  if (error) {
    console.error("❌ NewReleasesRail error:", error);
    return <></>;
  }

  // Show loading state or content
  return (
    <CollectionRail
      title="New Releases"
      items={titles}
      cardType={cardType}
      loading={loading}
      transitionTo={transitionTo}
    />
  );
}
